import momentTimezone from 'moment-timezone';
import {
  AUS_TIMEZONE_LIST_ONLY_LABEL,
  MELBOURNE_TIMEZONE,
} from '../constants/timezone';
import { TIME_FORMAT } from '../constants/time';

const STATE_TIMEZONE = {
  NSW: 'Australia/Sydney',
  ACT: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  WA: 'Australia/Perth',
  TAS: 'Australia/Hobart',
  NT: 'Australia/Darwin',
};

const STATE_NAME_TO_CODE = {
  'new south wales': 'NSW',
  'australian capital territory': 'ACT',
  victoria: 'VIC',
  queensland: 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  tasmania: 'TAS',
  'northern territory': 'NT',
};

const SPECIAL_POSTCODE_TIMEZONE = {
  2880: 'Australia/Broken_Hill',
  2898: 'Australia/Lord_Howe',
};

const POSTCODE_RANGES = [
  { min: 800, max: 999, timezone: 'Australia/Darwin' },
  { min: 1000, max: 2999, timezone: 'Australia/Sydney' },
  { min: 3000, max: 3999, timezone: 'Australia/Melbourne' },
  { min: 4000, max: 4999, timezone: 'Australia/Brisbane' },
  { min: 5000, max: 5999, timezone: 'Australia/Adelaide' },
  { min: 6000, max: 6999, timezone: 'Australia/Perth' },
  { min: 7000, max: 7999, timezone: 'Australia/Hobart' },
  { min: 8000, max: 8999, timezone: 'Australia/Melbourne' },
  { min: 9000, max: 9999, timezone: 'Australia/Brisbane' },
];

const isSelectableTimezone = (timezone) =>
  Boolean(timezone) && AUS_TIMEZONE_LIST_ONLY_LABEL.includes(timezone);

export const getTimezoneFromPostcode = (postcode) => {
  const parsed = parseInt(String(postcode ?? '').trim(), 10);

  if (!Number.isInteger(parsed)) return '';

  const specialTimezone = SPECIAL_POSTCODE_TIMEZONE[parsed];
  if (specialTimezone) return specialTimezone;

  const matchedRange = POSTCODE_RANGES.find(
    ({ min, max }) => parsed >= min && parsed <= max
  );

  return matchedRange ? matchedRange.timezone : '';
};

export const getTimezoneFromState = (state) => {
  const normalised = String(state ?? '')
    .trim()
    .toLowerCase();

  if (!normalised) return '';

  const stateCode =
    STATE_NAME_TO_CODE[normalised] || normalised.toUpperCase().replace('.', '');

  return STATE_TIMEZONE[stateCode] || '';
};

/**
 * The booking's timezone from the patient's address. With `fallbackTimezone`, an unknown
 * address uses it rather than the phone's zone.
 */
export const getTimezoneFromAddress = (
  addressResidential,
  { fallbackTimezone } = {}
) => {
  const postcodeTimezone = getTimezoneFromPostcode(
    addressResidential?.postcode
  );
  if (isSelectableTimezone(postcodeTimezone)) return postcodeTimezone;

  const stateTimezone = getTimezoneFromState(addressResidential?.state);
  if (isSelectableTimezone(stateTimezone)) return stateTimezone;

  if (fallbackTimezone) return fallbackTimezone;

  const guessedTimezone = momentTimezone.tz.guess();

  return isSelectableTimezone(guessedTimezone)
    ? guessedTimezone
    : MELBOURNE_TIMEZONE;
};

// The current month (YYYYMM) in the booking's timezone, not the phone's.
export const getCurrentMonth = (timezone) =>
  momentTimezone().tz(timezone).format(TIME_FORMAT.MONTH);
